
// Deployment trigger: 2026-04-24
import React from 'react';
import { Trophy } from 'lucide-react';
import { GameType } from '../../types';

interface MysteryBoxProps {
  type: GameType;
  gameState: string;
  activeSelection: number | null;
  onStart: (index: number) => void;
  theme?: string;
  isLightMode?: boolean;
}

const MysteryBox: React.FC<MysteryBoxProps> = ({ type, gameState, activeSelection, onStart, theme = 'classic', isLightMode }) => {
  const isNeon = theme === 'neon';
  const isLuxury = theme === 'luxury';
  const isLight = isLightMode;

  let bgColor = 'bg-[#0b0f1a]';
  let borderColor = 'border-[#1e293b]';
  let titleColor = 'text-white';
  let boxBg = 'bg-slate-900/50 hover:bg-slate-800/80';
  let boxBorder = 'border-slate-800 hover:border-slate-700';
  let activeBoxBorder = 'border-indigo-500 ring-4 ring-indigo-500/20';
  
  // Use a high quality emoji or image URL for the box
  let boxImage = "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=400&q=80&auto=format&fit=crop"; // Red gift box with gold ribbon

  if (isLight) {
    bgColor = 'bg-white';
    borderColor = 'border-slate-200';
    titleColor = 'text-slate-800';
    boxBg = 'bg-slate-50 hover:bg-slate-100';
    boxBorder = 'border-slate-200 hover:border-slate-300';
    activeBoxBorder = 'border-indigo-500 ring-4 ring-indigo-500/20';
  } else if (isNeon) {
    bgColor = 'bg-black';
    borderColor = 'border-fuchsia-600 shadow-[0_0_15px_#c026d3]';
    titleColor = 'text-cyan-400 text-shadow-[0_0_10px_#22d3ee]';
    boxBg = 'bg-black hover:bg-slate-900';
    boxBorder = 'border-fuchsia-600 shadow-[0_0_10px_#c026d3]';
    activeBoxBorder = 'border-cyan-400 ring-4 ring-cyan-400/50 shadow-[0_0_20px_#22d3ee]';
    boxImage = "https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=400&q=80&auto=format&fit=crop"; // Neon/colorful box
  } else if (isLuxury) {
    bgColor = 'bg-[#111]';
    borderColor = 'border-[#c29b42]';
    titleColor = 'text-[#e0b953]';
    boxBg = 'bg-[#1a1a1a] hover:bg-[#222]';
    boxBorder = 'border-[#c29b42]';
    activeBoxBorder = 'border-white ring-4 ring-white/20 shadow-[0_0_15px_#c29b42]';
    boxImage = "https://images.unsplash.com/photo-1608755728617-aefab37d2edd?w=400&q=80&auto=format&fit=crop"; // Luxury/gold box
  }

  return (
    <div className={`flex flex-col items-center w-full px-6 py-12 ${bgColor} rounded-[48px] shadow-2xl border ${borderColor}`}>
      <div className="text-center mb-10">
        <h3 className={`${titleColor} font-black text-2xl uppercase tracking-widest italic`}>
          Pick a Treasure
        </h3>
      </div>
      <div className="grid grid-cols-3 gap-4 w-full max-w-[400px] mb-12">
        {[0, 1, 2].map(i => (
          <div 
            key={i} 
            onClick={() => gameState === 'idle' && onStart(i)} 
            className={`group relative aspect-square rounded-[32px] border-2 transition-all cursor-pointer flex items-center justify-center overflow-hidden ${activeSelection === i ? activeBoxBorder : boxBorder} ${boxBg}`}
          >
            {gameState === 'finished' && activeSelection === i ? (
              <Trophy className="w-16 h-16 text-amber-400 animate-in zoom-in duration-500" />
            ) : (
              <div className="w-full h-full relative p-4 transition-transform duration-300 group-hover:scale-110">
                <img 
                  src={boxImage} 
                  alt="Mystery Box" 
                  className="w-full h-full object-cover rounded-2xl shadow-lg mix-blend-luminosity group-hover:mix-blend-normal transition-all duration-300"
                  referrerPolicy="no-referrer"
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest opacity-60">Tap to reveal its contents</p>
    </div>
  );
};

export default MysteryBox;
